/**
 * Shared NodeEditor field styles.
 *
 * The parameter field components under `WorkflowDesigner/NodeEditor/fields`
 * all share the same container/label scaffolding: a vertical flex container,
 * a label row that spreads its children, the label typography, and the
 * required marker. These four `GriffelStyle` fragments are referenced inside
 * each field's own `makeStyles` so the atomic classes stay identical while the
 * definitions live in one place.
 *
 * Usage:
 * ```ts
 * const useStyles = makeStyles({
 *   container: fieldContainerStyle,
 *   labelRow: fieldLabelRowStyle,
 *   label: fieldLabelStyle,
 *   required: fieldRequiredStyle,
 *   // ...field-specific rules
 * });
 * ```
 */

import { tokens, type GriffelStyle } from '@coreai/fluentui-react';

/** Vertical flex container wrapping a field's label row and control. */
export const fieldContainerStyle: GriffelStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: tokens.spacingVerticalXS,
};

/** Row holding the label (and optional trailing controls), spaced apart. */
export const fieldLabelRowStyle: GriffelStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

/** Field label typography. */
export const fieldLabelStyle: GriffelStyle = {
  fontSize: tokens.fontSizeBase300,
  fontWeight: tokens.fontWeightMedium,
  color: tokens.colorNeutralForeground1,
};

/** Required indicator shown next to the label. */
export const fieldRequiredStyle: GriffelStyle = {
  color: tokens.colorPaletteRedForeground1,
  marginLeft: tokens.spacingHorizontalXXS,
};
